import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSQ, getSQByTaskId, updateSQ, deleteSQ, getAllSQ } from '../Api/sqApi';

// Async thunks
export const createStandardQuestion = createAsyncThunk(
    'sq/create',
    async (data, { rejectWithValue }) => {
        try {
            const response = await createSQ(data);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data?.detail || 'Failed to create standard question');
        }
    }
);

export const fetchSQByTaskId = createAsyncThunk(
    'sq/fetchByTaskId',
    async (taskId, { rejectWithValue }) => {
        try {
            const response = await getSQByTaskId(taskId);
            return { taskId, questions: response };
        } catch (error) {
            return rejectWithValue(error.response?.data?.detail || 'Failed to fetch standard questions');
        }
    }
);

export const updateStandardQuestion = createAsyncThunk(
    'sq/update',
    async (data, { rejectWithValue }) => {
        try {
            const response = await updateSQ(data);
            return { ...response, ...data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.detail || 'Failed to update standard question');
        }
    }
);

export const deleteStandardQuestion = createAsyncThunk(
    'sq/delete',
    async (id, { rejectWithValue }) => {
        try {
            await deleteSQ(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.detail || 'Failed to delete standard question');
        }
    }
);

const sqSlice = createSlice({
    name: 'sq',
    initialState: {
        questions: [],
        loading: false,
        fetchLoading: false,
        deleteLoading: false,
        error: null,
        currentQuestion: null,
        currentTaskId: null
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentQuestion: (state, action) => {
            state.currentQuestion = action.payload;
        },
        clearCurrentQuestion: (state) => {
            state.currentQuestion = null;
        },
        clearTaskStatuses: (state) => {
            state.questions = [];
            state.status = 'idle';
            state.error = null;
        },
        clearSQ: (state) => {
            state.questions = [];
            state.currentTaskId = null;
            state.fetchLoading = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create SQ
            .addCase(createStandardQuestion.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createStandardQuestion.fulfilled, (state, action) => {
                state.loading = false;
                state.questions = [...state.questions, action.payload];
            })
            .addCase(createStandardQuestion.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch SQ by Task ID
            .addCase(fetchSQByTaskId.pending, (state) => {
                state.fetchLoading = true;
                state.error = null;
                state.questions = [];
                state.currentTaskId = null;
            })
            .addCase(fetchSQByTaskId.fulfilled, (state, action) => {
                state.fetchLoading = false;
                state.currentTaskId = action.payload.taskId;
                state.questions = action.payload.questions;
            })
            .addCase(fetchSQByTaskId.rejected, (state, action) => {
                state.fetchLoading = false;
                state.error = action.payload;
            })

            // Update SQ
            .addCase(updateStandardQuestion.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateStandardQuestion.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.questions.findIndex(q => q.id === action.payload.id);
                if (index !== -1) {
                    state.questions[index] = {
                        ...state.questions[index],
                        ...action.payload
                    };
                }
            })
            .addCase(updateStandardQuestion.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Delete SQ
            .addCase(deleteStandardQuestion.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deleteStandardQuestion.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.questions = state.questions.filter(q => q.id !== action.payload);
            })
            .addCase(deleteStandardQuestion.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            });
    }
});

export const { clearError, setCurrentQuestion, clearCurrentQuestion, clearTaskStatuses, clearSQ } = sqSlice.actions;

// Selectors
export const selectSQ = (state) => state.sq.questions;
export const selectSQLoading = (state) => state.sq.loading;
export const selectSQFetchLoading = (state) => state.sq.fetchLoading;
export const selectSQDeleteLoading = (state) => state.sq.deleteLoading;
export const selectSQError = (state) => state.sq.error;
export const selectCurrentSQ = (state) => state.sq.currentQuestion;

export default sqSlice.reducer;
